import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { StatusBadge } from '../components/common/StatusBadge';
import { PlatformIcon } from '../components/common/PlatformIcon';
import { postsApi } from '../lib/api';
import { CalendarDays, List, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function CompanyCalendar() {
  const { companyId } = useOutletContext();
  const navigate = useNavigate();
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Fetch all posts for the calendar
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['posts', companyId, 'calendar'],
    queryFn: () => postsApi.list(companyId, { limit: 100 }).then((r) => r.data),
  });

  // Group posts by date
  const postsByDate = useMemo(() => {
    const map = {};
    if (postsData?.posts) {
      postsData.posts.forEach((post) => {
        const date = post.scheduled_at || post.created_at;
        if (date) {
          const key = new Date(date).toISOString().split('T')[0];
          if (!map[key]) map[key] = [];
          map[key].push(post);
        }
      });
    }
    return map;
  }, [postsData]);

  // Posts for selected date
  const selectedDateKey = selectedDate?.toISOString().split('T')[0];
  const selectedPosts = postsByDate[selectedDateKey] || [];

  // Upcoming posts (sorted)
  const upcomingPosts = useMemo(() => {
    if (!postsData?.posts) return [];
    return postsData.posts
      .filter((p) => p.status === 'scheduled' && p.scheduled_at)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [postsData]);

  // Dates that have posts
  const datesWithPosts = useMemo(() => {
    return Object.keys(postsByDate).map((d) => new Date(d));
  }, [postsByDate]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold font-display">Content Calendar</h2>
          <div className="flex items-center gap-2 mt-1">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Europe/Istanbul</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={view === 'month' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setView('month')}
            data-testid="calendar-month-view"
          >
            <CalendarDays className="w-4 h-4 mr-1" /> Month
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setView('list')}
            data-testid="calendar-list-view"
          >
            <List className="w-4 h-4 mr-1" /> List
          </Button>
        </div>
      </div>

      {view === 'month' ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
          {/* Calendar */}
          <Card>
            <CardContent className="p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                modifiers={{
                  hasPost: datesWithPosts,
                }}
                modifiersStyles={{
                  hasPost: {
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                    textDecorationColor: 'hsl(172 66% 36%)',
                    textUnderlineOffset: '4px',
                  },
                }}
                className="w-full"
              />
            </CardContent>
          </Card>

          {/* Selected day detail */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">
                {selectedDate?.toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedPosts.length > 0 ? (
                <div className="space-y-3">
                  {selectedPosts.map((post) => (
                    <button
                      key={post._id}
                      onClick={() => navigate(`/companies/${companyId}/posts/${post._id}/edit`)}
                      className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{post.title || post.caption?.substring(0, 40)}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {post.platform_targets?.map((p) => (
                              <PlatformIcon key={p} platform={p} size="xs" />
                            ))}
                          </div>
                          {post.scheduled_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(post.scheduled_at).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          )}
                        </div>
                        <StatusBadge status={post.status} />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No posts for this day
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* List View */
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">Upcoming Scheduled Posts</CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingPosts.length > 0 ? (
              <div className="space-y-3">
                {upcomingPosts.map((post) => (
                  <button
                    key={post._id}
                    onClick={() => navigate(`/companies/${companyId}/posts/${post._id}/edit`)}
                    className="w-full text-left p-3 rounded-lg border hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="text-lg font-semibold font-display tabular-nums">
                          {new Date(post.scheduled_at).getDate()}
                        </p>
                        <p className="text-xs text-muted-foreground uppercase">
                          {new Date(post.scheduled_at).toLocaleDateString('en-GB', { month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{post.title || post.caption?.substring(0, 50)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {post.platform_targets?.map((p) => (
                            <PlatformIcon key={p} platform={p} size="xs" />
                          ))}
                          <span className="text-xs text-muted-foreground">
                            {new Date(post.scheduled_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <StatusBadge status={post.status} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No upcoming scheduled posts</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
